import { cn } from "@workspace/web-ui/lib/utils";

interface SkeletonProps {
  className?: string;
}

const TimelineSkeleton = ({ className }: SkeletonProps) => {
  return (
    <div className={cn("space-y-4 rounded-lg bg-[#e3e2e2] p-4", className)}>
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex gap-5 items-start w-full animate-pulse">
          <div className="w-6 h-6 rounded-full bg-gray-300" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-1/4 bg-gray-300 rounded" />
            <div className="h-3 w-3/4 bg-gray-300 rounded" />
            <div className="flex gap-2 items-center">
              <div className="h-3 w-20 bg-gray-300 rounded" />
              <div className="h-3 w-3 bg-gray-300 rounded-full" />
              <div className="h-3 w-20 bg-gray-300 rounded" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const MedicationSkeleton = ({ className }: SkeletonProps) => {
  return (
    <div className={cn("space-y-4 rounded-lg bg-white p-4", className)}>
      {[1, 2].map((i) => (
        <div key={i} className="flex flex-col gap-3 animate-pulse">
          <div className="h-5 w-1/3 bg-gray-200 rounded" />
          <div className="h-4 w-2/3 bg-gray-200 rounded" />
          <div className="h-4 w-1/4 bg-gray-200 rounded" />
        </div>
      ))}
    </div>
  );
};

const RefillsSkeleton = ({ className }: SkeletonProps) => {
  return (
    <div className={cn("space-y-4 rounded-lg bg-white p-4", className)}>
      {[1, 2].map((i) => (
        <div key={i} className="flex flex-col gap-3 animate-pulse">
          <div className="h-5 w-1/3 bg-gray-200 rounded" />
          <div className="h-4 w-2/3 bg-gray-200 rounded" />
          <div className="h-4 w-1/4 bg-gray-200 rounded" />
        </div>
      ))}
    </div>
  );
};

const ChatHistorySkeleton = () => (
  <div className="animate-pulse space-y-2">
    <div className="h-4 bg-gray-200 rounded w-3/4" />
    <div className="h-4 bg-gray-200 rounded w-1/2" />
    <div className="h-4 bg-gray-200 rounded w-2/3" />
    <div className="h-4 bg-gray-200 rounded w-1/4" />
  </div>
);

const MedicalPlanSkeleton = ({ className }: SkeletonProps) => {
  return (
    <div className={cn("space-y-6 rounded-lg bg-white p-4", className)}>
      <div className="animate-pulse space-y-4">
        <div className="h-6 w-1/3 bg-gray-200 rounded" />
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-4 w-full bg-gray-200 rounded" />
          ))}
        </div>
      </div>
    </div>
  );
};

const QuestionnaireSkeleton = ({ className }: SkeletonProps) => {
  return (
    <div className={cn("space-y-6 rounded-lg bg-white p-4", className)}>
      {[1, 2].map((i) => (
        <div key={i} className="animate-pulse space-y-3">
          <div className="h-5 w-1/4 bg-gray-200 rounded" />
          <div className="space-y-2">
            <div className="h-4 w-3/4 bg-gray-200 rounded" />
            <div className="h-4 w-2/3 bg-gray-200 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
};

const PaymentHistorySkeleton = ({ className }: SkeletonProps) => {
  return (
    <div className={cn("space-y-4 rounded-lg bg-white p-4", className)}>
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex justify-between items-center animate-pulse">
          <div className="space-y-2">
            <div className="h-4 w-32 bg-gray-200 rounded" />
            <div className="h-3 w-24 bg-gray-200 rounded" />
          </div>
          <div className="h-4 w-20 bg-gray-200 rounded" />
        </div>
      ))}
    </div>
  );
};

const NotesSkeleton = ({ className }: SkeletonProps) => {
  return (
    <div className={cn("space-y-4 rounded-lg bg-white p-4", className)}>
      {[1, 2].map((i) => (
        <div key={i} className="animate-pulse space-y-3">
          <div className="flex justify-between items-center">
            <div className="h-5 w-1/4 bg-gray-200 rounded" />
            <div className="h-4 w-24 bg-gray-200 rounded" />
          </div>
          <div className="space-y-2">
            <div className="h-4 w-full bg-gray-200 rounded" />
            <div className="h-4 w-3/4 bg-gray-200 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
};

const VerificationSkeleton = ({ className }: SkeletonProps) => {
  return (
    <div className={cn("space-y-4 rounded-lg bg-white p-4", className)}>
      <div className="animate-pulse space-y-4">
        <div className="h-6 w-1/3 bg-gray-200 rounded" />
        <div className="space-y-3">
          <div className="h-4 w-full bg-gray-200 rounded" />
          <div className="h-4 w-3/4 bg-gray-200 rounded" />
          <div className="h-4 w-2/3 bg-gray-200 rounded" />
        </div>
        <div className="h-64 w-full bg-gray-200 rounded" />
        <div className="h-64 w-full bg-gray-200 rounded" />
      </div>
    </div>
  );
};

export {
  TimelineSkeleton,
  MedicationSkeleton,
  MedicalPlanSkeleton,
  QuestionnaireSkeleton,
  PaymentHistorySkeleton,
  NotesSkeleton,
  ChatHistorySkeleton,
  RefillsSkeleton,
  VerificationSkeleton
}; 